import { parseSkillManifest, type SkillParameter } from './skillParameters';

export type SkillSource = 'github' | 'marketplace' | 'custom';

/**
 * Org governance state for a skill (Skills 1.2). `requested` = a member-proposed,
 * not-yet-installed skill (shown in the Requested review queue); `pending` =
 * installed but never reviewed; `approved` = on the org allowlist; `blocked` =
 * deliberately withheld by an admin. Only meaningful when the allowlist is on.
 */
export type SkillApprovalStatus = 'requested' | 'pending' | 'approved' | 'blocked';

/** View-model shape consumed by the VM + page. */
export interface InstalledSkill {
  id: string;
  orgId: string;
  source: SkillSource;
  name: string;
  repoUrl: string | null;
  /** Pinned ref (e.g. v2.1); null when installed unpinned. */
  pinnedVersion: string | null;
  enabled: boolean;
  /** Governance state (Skills 1.2). */
  approvalStatus: SkillApprovalStatus;
  /** Who set approved/blocked, and when (honest attribution). */
  approvedBy: string | null;
  approvedAt: string | null;
  /** Who requested it, and when (Requested queue rows). */
  requestedBy: string | null;
  requestedAt: string | null;
  /**
   * Captured skill manifest (Skills 1.5, PRD Option A) — populated from the
   * skill's `SKILL.md` frontmatter at install. NULL when none was captured.
   */
  manifest: unknown | null;
  /**
   * Declared parameters derived from {@link manifest} (the curated-form seam).
   * Empty → the skill instant-inserts a bare chip; non-empty → it opens the
   * curated card (Skills 1.5).
   */
  parameters: SkillParameter[];
  /**
   * Skills 1.7 — the skill-files bucket prefix where the actual file set lives
   * (`org/<orgId>/skill/<skillId>/<version>/`). NULL until the files are stored
   * (a backfill-pending row). See {@link stored}.
   */
  storagePath: string | null;
  /** Skills 1.7 — integrity hash over the stored file set (1.8 verifies it). */
  contentHash: string | null;
  /** Skills 1.7 — the stored file list ([{ path, size, sha256 }]); NULL until stored. */
  fileManifest: unknown | null;
  /**
   * Skills 1.7 — true once the skill's files are stored in Supabase (the runtime
   * can materialize it). Derived from {@link storagePath}. A GitHub install is
   * atomic, so an installed github row is normally stored; a row may be unstored
   * only while a backfill is pending.
   */
  stored: boolean;
}

/** Raw `public.org_skills` row shape (subset the UI reads). */
export interface OrgSkillRow {
  id: string;
  org_id: string;
  source: string;
  name: string;
  repo_url: string | null;
  pinned_version: string | null;
  enabled: boolean;
  installed_by: string | null;
  created_at: string;
  updated_at: string;
  /** Skills 1.2 governance columns. */
  approval_status: string;
  approved_by: string | null;
  approved_at: string | null;
  requested_by: string | null;
  requested_at: string | null;
  /** Skills 1.5 — captured `SKILL.md` manifest (Option A). NULL when none. */
  manifest?: unknown | null;
  /** Skills 1.7 — stored-file pointer columns (NULL until materialized). */
  storage_path?: string | null;
  content_hash?: string | null;
  file_manifest?: unknown | null;
}

function toApprovalStatus(s: string | null | undefined): SkillApprovalStatus {
  return s === 'requested' || s === 'approved' || s === 'blocked' ? s : 'pending';
}

export function rowToInstalledSkill(r: OrgSkillRow): InstalledSkill {
  return {
    id: r.id,
    orgId: r.org_id,
    source:
      r.source === 'marketplace' ? 'marketplace' : r.source === 'custom' ? 'custom' : 'github',
    name: r.name,
    repoUrl: r.repo_url,
    pinnedVersion: r.pinned_version,
    enabled: r.enabled,
    approvalStatus: toApprovalStatus(r.approval_status),
    approvedBy: r.approved_by ?? null,
    approvedAt: r.approved_at ?? null,
    requestedBy: r.requested_by ?? null,
    requestedAt: r.requested_at ?? null,
    manifest: r.manifest ?? null,
    parameters: parseSkillManifest(r.manifest ?? null),
    storagePath: r.storage_path ?? null,
    contentHash: r.content_hash ?? null,
    fileManifest: r.file_manifest ?? null,
    stored: (r.storage_path ?? null) !== null
  };
}

/**
 * The effective governed `usable` signal a consumer reads (Skills 1.2). With the
 * allowlist off (permissive default) every skill is usable; with it on, only an
 * `approved` skill is. Combine with the member `enabled` toggle to decide whether
 * a skill actually runs (`usable && enabled`). A non-usable skill renders
 * "blocked by org policy" — never a silent failure.
 */
export function computeUsable(
  allowlistEnabled: boolean,
  status: SkillApprovalStatus
): boolean {
  return allowlistEnabled ? status === 'approved' : true;
}

export type GithubSkillParse =
  | { ok: true; repoUrl: string; owner: string; repo: string; name: string }
  | { ok: false; error: string };

/**
 * Parse + validate a GitHub repo URL for skill install. Accepts
 * `https://github.com/owner/repo`, `github.com/owner/repo`, optional `.git`
 * and trailing slash. Canonicalizes to `https://github.com/owner/repo`. The
 * default skill `name` is the repo segment (the user may override it in the form).
 */
export function parseGithubSkillUrl(input: string): GithubSkillParse {
  const raw = (input ?? '').trim();
  if (!raw) return { ok: false, error: 'empty' };

  const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let url: URL;
  try {
    url = new URL(withProto);
  } catch {
    return { ok: false, error: 'invalid_url' };
  }
  if (url.hostname.toLowerCase() !== 'github.com') {
    return { ok: false, error: 'not_github' };
  }
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length < 2) return { ok: false, error: 'missing_owner_repo' };

  const owner = segments[0];
  const repo = segments[1].replace(/\.git$/i, '');
  if (!owner || !repo) return { ok: false, error: 'missing_owner_repo' };

  return {
    ok: true,
    repoUrl: `https://github.com/${owner}/${repo}`,
    owner,
    repo,
    name: repo
  };
}
